import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { map, take } from 'rxjs/operators';
import { environment } from '@env/environment';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';


const httpOptionsFormData = {
  headers: new HttpHeaders({
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

@Injectable(
  { providedIn: 'root' }
)
export class SharedService {
  public mobile: boolean = false;
  private changeTheme: Subject<string> = new Subject<string>();
  public darkMode: boolean = false;
  public userTheme: string = environment.theme;
  private api: string = environment.privateApi;
  public loading: boolean = false;
  public tablet: boolean = false;
  public purple: string = '#ACA2D0';
  public blue: string = '#628ef4';
  public gold: string = '#ffbe2e';
  public green: string = '#70e17b';
  public pink: string = '#e9695f';
  public orange: string = '#EC891D';
  public red: string = '#fc4132'

  //Shared application statuses  
  public pendingApplicationStatus = "InProgress";

  stateList: string[] = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
    "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
    "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];

  countyList: string[] = [
    "Baker", "Benton", "Clackamas", "Clatsop", "Columbia", "Coos", "Crook",
    "Curry", "Deschutes", "Douglas", "Gilliam", "Grant", "Harney", "Hood River",
    "Jackson", "Jefferson", "Josephine", "Klamath", "Lake", "Lane", "Lincoln",
    "Linn", "Malheur", "Marion", "Morrow", "Multnomah", "Polk", "Sherman",
    "Tillamook", "Umatilla", "Union", "Wallowa", "Wasco", "Washington", "Wheeler",
    "Yamhill"
  ];

  public dialogRef: MatDialogRef<ConfirmDialogComponent>;

  constructor(private httpClient: HttpClient, public dialog: MatDialog, public toastr: ToastrService) { }

  public openConfirm(message: string): void{
    this.dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: message,
      minWidth: !this.mobile? '400px' : '',
      maxWidth: !this.mobile? '700px' : '300px',
      maxHeight: this.mobile? '600px' : '700px',
    });
  }

  /**
   * Standard sizing for full-page wizard dialogs (multi step forms).
   * The dialog is bound to the viewport so its internal flex layout always has a
   * known height to fill, which keeps the action bar and every field reachable
   * at any screen size, zoom level or display scaling.
   */
  public getWizardDialogConfig<D = any>(overrides: MatDialogConfig<D> = {}): MatDialogConfig<D> {
    return {
      width: this.mobile ? '100vw' : '95vw',
      maxWidth: this.mobile ? '100vw' : '1600px',
      height: '95vh',
      maxHeight: '95vh',
      panelClass: 'app-wizard-dialog',
      autoFocus: false,
      ...overrides
    };
  }

  public translateStatus(status: string) : string {
    if(status == this.pendingApplicationStatus) {
      return "In Progress";
    }

    return status;
  }

  public confirmed(): Observable<any>{
    return this.dialogRef.afterClosed().pipe(take(1), map(res => {
      return res;
    }));
  }

  get isMobile(): boolean {
    return this.mobile;
  }

  get counties(): string[] {
    return this.countyList;
  }

  get states(): string[] {
    return this.stateList;
  }

  get hoverBackgroundColor(): string {
    return this.darkMode ? '#333333' : '#C5CAE9';
  }

  getStatusColor(status: string): string {
    let lightColor: string;
    let darkColor: string;

    switch (status) {
      case 'Submitted':
        lightColor = '#ffe599';
        darkColor = '#b39f00';
        break;
      case 'In Progress':
      case 'Pending':
        lightColor = '#bcbcbc';
        darkColor = '#6b6b6b';
        break;
      case 'Cancelled':
        lightColor = '#cc0000';
        darkColor = '#800000';
        break;
      case 'Approved':
      case 'Processed':
        lightColor = '#8fce00';
        darkColor = '#5a8000';
        break;
      case 'In Review':
        lightColor = '#2986cc';
        darkColor = '#1a5280';
        break;
      case 'Action Needed':
        lightColor = '#e69138';
        darkColor = '#995e00';
        break;
      case 'Expired':
        lightColor = '#f9cb9c';
        darkColor = '#b45f06';
        break;
      default:
        lightColor = '#ffffff';
        darkColor = '#cccccc';
        break;
    }

    return this.darkMode ? darkColor : lightColor;
  }

  themeChanged$ = this.changeTheme.asObservable();

  toggleTheme() {
    this.darkMode = this.userTheme === 'dark';
    this.changeTheme.next(this.userTheme);
  }

  updateUserTheme(): void {
    this.httpClient.post<boolean>(this.api + "UserProfile/UpdateUserTheme/" +  this.userTheme, null).subscribe(
      () => { },
      error => {
        console.log(error);
       }
    );
  }

  getUserTheme(): Observable<string> {
    return this.httpClient.get<string>(this.api +  "UserProfile/GetUserTheme/", httpOptionsFormData);
  }

}
